<template>
  <!-- Loading state -->
  <template v-if="!vehicleDetail && !store.error">
    <div :style="{ textAlign: 'center' }">
      <v-progress-circular
        :size="100"
        :width="8"
        indeterminate
        color="primary"
        class="mt-15 text-center w-100"
        :style="{ maxWidth: '100px' }"
      ></v-progress-circular>
    </div>
  </template>

  <!-- No model data available -->
  <template
    v-else-if="store.error || (vehicleDetail?.trims ?? []).length === 0"
  >
    <v-card class="mt-lg-15 mx-auto text-center w-25" variant="tonal">
      <v-card-title
        v-if="
          year !== 'undefined' && make !== 'undefined' && model !== 'undefined'
        "
        class="text-capitalize"
        >{{ year }} {{ make }} {{ model }}</v-card-title
      >
      <v-card-text>No model data available.</v-card-text>
    </v-card>
  </template>

  <!-- Model data available -->
  <template v-else>
    <!-- Header -->
    <div
      class="lightning-model-page__banner"
      :style="{ backgroundImage: `url(${backdropImageUrl})` }"
    >
      <v-img
        :src="headerImage"
        :alt="`${currentVehicleName} in ${currentColorName} color`"
        class="lightning-model-page__banner__car-image"
        max-width="500"
        width="100%"
        height="auto"
        :eager="true"
      />
      <template
        v-if="false"
        v-for="(imgUrl, idx) in headerCarImages"
        :key="`header-image-${idx}`"
      >
        <v-img
          :src="imgUrl"
          :alt="`${currentVehicleName} in ${currentColorName} color`"
          class="lightning-model-page__banner__car-image"
          max-width="500"
          width="100%"
          height="auto"
          :eager="true"
        />
      </template>
    </div>

    <!-- Model Name and Price -->
    <v-container
      max-width="1280"
      class="d-flex flex-column flex-md-row lightning-model-page__header align-center justify-space-between mb-0"
    >
      <div class="order-0 mb-4 mb-md-0">
        <!-- Model Name -->
        <div class="lightning-model-page__header__model-name mt-3 mt-md-0">
          {{ pageSettings?.year ?? '' }} {{ pageSettings?.make ?? '' }}
          {{ pageSettings?.model ?? '' }}
        </div>

        <!-- Trim Style -->
        <div
          class="lightning-model-page__header__trim-style text-grey-darken-1 d-flex justify-center justify-md-end mt-3"
        >
          {{ selectedTrim?.styleName }}
        </div>
      </div>
      <div class="order-3">
        <!-- Price -->
        <div class="lightning-model-page__header__price mt-3 mt-md-0">
          {{
            !selectedTrim?.price
              ? 'TBD'
              : formatCurrency(Number(selectedTrim?.price))
          }}
        </div>
        <!-- Price Label -->
        <div
          class="lightning-model-page__header__trim-style text-center text-md-right text-grey-darken-1 mt-3"
        >
          MSRP
        </div>
      </div>
    </v-container>

    <!-- CTAs -->
    <div class="lightning-model-page__CTAS-group px-10 py-0 mb-5">
      <v-container class="d-flex flex-column flex-md-row">
        <CallToAction
          v-if="primaryCTA != null && primaryCTA.link !== ''"
          :settings="primaryCTA"
          class="w-100 flex-shrink-1 my-2 my-md-0 mx-0 mx-md-2"
        />
        <CallToAction
          v-if="secondaryCTA != null && secondaryCTA.link !== ''"
          :settings="secondaryCTA"
          class="w-100 flex-shrink-1 my-2 my-md-0 mx-0 mx-md-2"
        />
      </v-container>
    </div>

    <!-- Color Swatch -->
    <div
      class="lightning-model-page__colors mb-10"
      v-if="colors && colors.length"
    >
      <div class="text-center mb-5">
        <h2 style="color: white">Color</h2>
        <p>
          {{
            colors[selectedColorIndex]
              ? colors[selectedColorIndex].colorName
              : 'TBD'
          }}
        </p>
      </div>
      <div>
        <v-item-group
          mandatory
          class="d-flex justify-center align-start flex-wrap ga-2"
          v-on:update:model-value="selectColor"
          model-value="selectedColorIndex"
        >
          <v-item
            v-slot="{ isSelected, toggle }"
            v-for="(color, idx) in colors"
            :key="idx"
          >
            <div
              class="pb-1"
              :style="{
                borderBottom:
                  selectedColorIndex === idx ? '3px solid #fff' : '',
              }"
            >
              <div
                :style="{
                  width: '50px',
                  height: '50px',
                  backgroundColor: color.colorValue,
                  cursor: 'pointer',
                  border: '2px solid white',
                }"
                class="rounded-xl"
                @click="toggle"
              ></div>
            </div>
          </v-item>
        </v-item-group>
      </div>
    </div>

    <!-- Trims -->
    <div class="mb-10" v-if="trims && trims.length">
      <h2 class="text-center mb-5">TRIMS</h2>
      <div>
        <v-item-group
          mandatory
          v-on:update:model-value="selectTrim"
          :model-value="selectedTrimId"
        >
          <div class="lightning-model-page__trims-container">
            <div
              v-for="(trim, idx) in trims"
              :key="idx"
              class="lightning-model-page__trim-wrapper"
            >
              <v-item v-slot="{ isSelected, toggle }">
                <v-card
                  :class="`lightning-model-page__trim-card ${selectedTrimId === idx ? 'selected' : ''}`"
                  dark
                  @click="toggle"
                >
                  <v-card-text class="pa-4 d-flex flex-column">
                    <div class="lightning-model-page__trim-image-container">
                      <Suspense>
                        <template #default>
                          <AsyncImage :trim="trim" size="sm" />
                        </template>
                        <template #fallback>
                          <div class="loading">
                            <v-img
                              class="mx-auto"
                              :src="noVehicleImage"
                              width="100%"
                              contain
                            />
                          </div>
                        </template>
                      </Suspense>
                    </div>
                    <div
                      class="lightning-model-page__trim-info text-center mt-auto"
                    >
                      <p
                        :class="`trim-title ${isSelected ? '' : 'text-blue-darken-3'}`"
                      >
                        {{ trim.year }} {{ trim.make }} {{ trim.model }}
                        <span class="d-block">{{ trim.styleName }}</span>
                      </p>
                      <p class="trim-price">
                        <span v-if="Number(trim.price) > 0">
                          Starting at {{ formatCurrency(Number(trim.price)) }}
                        </span>
                        <span v-else>Call for Price</span>
                      </p>
                    </div>
                  </v-card-text>
                </v-card>
              </v-item>
            </div>
          </div>
        </v-item-group>
      </div>
    </div>

    <!-- Highlighted Features -->
    <template v-for="featuresSection in highlightedFeatures">
      <HighlightedFeaturesSection
        :featuresSection="featuresSection"
        :availableHighlightedFeatures="availableHighlightedFeatures"
      />
    </template>

    <!-- Tech Specs & Standard Equipments -->
    <div
      class="mx-auto mt-6 pa-0 lightning-model-page__model-details"
      style="max-width: 1200px"
    >
      <v-expansion-panels flat>
        <!-- Standard Equipment -->
        <template v-for="(value, key) in selectedTrim?.standardEquipment">
          <v-expansion-panel
            v-if="value.items.length"
            style="border-bottom: 1px solid #ddd"
          >
            <v-expansion-panel-title :key="key" class="text-subtitle-1">
              <template v-slot:default="{ expanded }">
                <div :class="`${expanded ? 'text-blue-darken-3' : ''}`">
                  {{ toPascalCase(String(key)) }}
                </div>
              </template>
              <template v-slot:actions="{ expanded }">
                <v-icon
                  :color="expanded ? 'blue-darken-3' : '#7a7a7a'"
                  :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                  :size="50"
                ></v-icon>
              </template>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <ul class="mx-5">
                <li
                  v-for="item in value.items"
                  :key="item"
                  class="text-grey-darken-1"
                >
                  {{ item }}
                </li>
              </ul>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </template>

        <!-- Technical Specification -->
        <v-expansion-panel
          v-if="
            selectedTrim?.technicalSpecification &&
            Object.keys(selectedTrim?.technicalSpecification).length
          "
        >
          <v-expansion-panel-title class="text-subtitle-1">
            <template v-slot:default="{ expanded }">
              <div :class="`${expanded ? 'text-blue-darken-3' : ''}`">
                Tech Specs
              </div>
            </template>
            <template v-slot:actions="{ expanded }">
              <v-icon
                :color="expanded ? 'blue-darken-3' : '#7a7a7a'"
                :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                :size="50"
              ></v-icon>
            </template>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <dl>
              <div
                v-for="(
                  value, key, idx
                ) in selectedTrim?.technicalSpecification"
                :key="key"
              >
                <dt :class="`${idx === 0 ? '' : 'mt-3'} mb-2`">{{ key }}</dt>
                <dd class="mx-5">
                  <ul>
                    <li
                      v-for="item in value.items"
                      :key="item"
                      class="text-grey-darken-1"
                    >
                      {{ item }}
                    </li>
                  </ul>
                </dd>
              </div>
            </dl>
          </v-expansion-panel-text>
        </v-expansion-panel>

        <!-- Consumer Information -->
        <v-expansion-panel
          v-if="
            selectedTrim?.consumerInformation?.Warranty?.items &&
            selectedTrim?.consumerInformation?.Warranty?.items?.length
          "
        >
          <v-expansion-panel-title class="text-subtitle-1">
            <template v-slot:default="{ expanded }">
              <div :class="`${expanded ? 'text-blue-darken-3' : ''}`">
                Warranty
              </div>
            </template>
            <template v-slot:actions="{ expanded }">
              <v-icon
                :color="expanded ? 'blue-darken-3' : '#7a7a7a'"
                :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                :size="50"
              ></v-icon>
            </template>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <ul class="mx-5">
              <li
                v-for="(item, idx) in selectedTrim?.consumerInformation.Warranty
                  .items"
                :key="idx"
                class="text-grey-darken-1"
              >
                {{ item }}
              </li>
            </ul>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </template>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import type { ComputedRef } from 'vue'
import HighlightedFeaturesSection from './HighlightedFeaturesSection.vue'
import AsyncImage from './AsyncImage.vue'
import type { ICallToAction } from '@jazel/jazel-vuetify'
import {
  formatCurrency,
  getAnyImageForTrim,
  getCDNUrl,
  getImageAsync,
  getImageServiceUrl,
  getSizeAndUnitCSS,
  getTrimColorizedImage,
  toPascalCase,
} from '@/utils'
import { fetchVehicleDetailByModel } from '@/apis'
import { useSettingsStore } from '@/store/settings'

import type {
  IFeaturesSectionSettings,
  IHeaderSettings,
  IModelDetailResponse,
  IPage,
} from '@/types'

import { useImageStore } from '@/store/images'
import type { IHeaderImageStore } from '@/store/type'
import CallToAction from '@/components/CallToAction.vue'

const imageStore = useImageStore()
const imageServiceUrl = getImageServiceUrl()
const cdnUrl = getCDNUrl()
const backdropImageUrl = `${cdnUrl}/lightning-model-page/cx/city-backdrop.jpg`
const noVehicleImage = `${cdnUrl}/lightning-model-page/cx/no-vehicle-image.png`

// const route = useRoute()
const year = ref('')
const make = ref('')
const model = ref('')

const store = useSettingsStore()
const globalSettings = computed(() => store.settings?.globalSettings)
const pageSettings = ref<IPage | null>(store.pageSettings ?? null)
const highlightedFeatures = computed<IFeaturesSectionSettings[]>(
  () => pageSettings.value?.highlightedFeatures ?? [],
)
const fallbackImageUrl = ref<string | null>(null)

const primaryCTA = ref<ICallToAction | any>(
  pageSettings.value?.overrideGlobalPrimaryCTA
    ? (pageSettings.value?.primaryCTA ?? {})
    : (globalSettings.value?.primaryCTA ?? {}),
)
const secondaryCTA = ref<ICallToAction | any>(
  pageSettings.value?.overrideGlobalSecondaryCTA
    ? (pageSettings.value?.secondaryCTA ?? {})
    : (globalSettings.value?.secondaryCTA ?? {}),
)

const availableHighlightedFeaturesMapping: { [key: string]: string } = {
  batteryWarranty: 'Battery Warranty',
  blindSpotMonitor: 'BLIS (Blind Spot Information System)',
  bodyStyle: 'Body Style',
  chargingSpeed: 'Charging Battery Speed',
  drivetrain: 'Drivetrain Type',
  engineType: 'Engine Type',
  estimatedBatteryRange: 'Estimated Battery Range',
  fuelEconomy: 'Fuel Economy',
  headRoom: 'Head Room',
  heatedSeat: 'Heated seat',
  horsepower: 'Horsepower and Torque',
  laneKeepingAssist: 'Lane Keeping Alert Lane Keeping Assist',
  legRoom: 'Leg Room',
  seatingCapacity: 'Seating Capacity',
  torque: 'Horsepower and Torque',
  transmission: 'Transmission Type',
}

const vehicleDetail = ref<IModelDetailResponse | null>(null)
const selectedTrimId = ref(0)
const selectedColorIndex = ref(0)
const headerImage = ref<string>('')
const headerCarImages = ref<string[]>([])

const currentColorHex = ref('')
const currentColorName = ref('')
const currentVehicleName = ref('')

const trims = computed(() => {
  if (vehicleDetail.value === null || !vehicleDetail.value.trims) return []
  return vehicleDetail.value.trims
})

const selectedTrim = ref<any | null>(null)

const colors = ref<any[]>([])

const injectSchema = (schema: any) => {
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.textContent = JSON.stringify(schema)
  document.head.appendChild(script)
}

const getTechSpecs = (
  trimData: any,
  key: string,
  substring: string,
  defaultValue: string = '',
) => {
  const technicalSpecification: string[] =
    trimData?.technicalSpecification?.[key]?.items ?? []
  const item = technicalSpecification.find(
    (item: string) => item.indexOf(substring) >= 0,
  )

  if (!item) {
    return defaultValue
  }

  const titleValuePair = item.split(':')

  return titleValuePair[1] ?? titleValuePair[0]
}

const getStdEquip = (
  trimData: any,
  key: string,
  substring: string,
  defaultValue: string = '',
) => {
  const technicalSpecification: string[] =
    trimData?.standardEquipment?.[key]?.items ?? []
  const item = technicalSpecification.find(
    (item: string) => item.indexOf(substring) >= 0,
  )

  if (!item) {
    return defaultValue
  }

  const titleValuePair = item.split(':')

  return titleValuePair[1] ?? titleValuePair[0]
}

const getSchema = (trimData: any) => {
  const image = getAnyImageForTrim(trimData)

  const year = trimData.year ?? ''
  const make = trimData.make ?? ''
  const model = trimData.model ?? ''
  const trim = trimData.trim ?? ''
  const styleName = trimData.styleName ?? ''
  const condition = 'New'
  const price = trimData.price
  const vehicleType = getTechSpecs(trimData, 'Summary', 'Body Style', 'Car')
  const passengerCapacity = getTechSpecs(
    trimData,
    'Interior Dimensions',
    'Passenger Capacity',
    '',
  )
  const drivetrain = getTechSpecs(trimData, 'Transmission', 'Drivetrain', '')
  const mileage = getTechSpecs(
    trimData,
    'Mileage',
    'Fuel Economy Est-Combined',
    '0',
  )
  const exteriorColor = trimData?.images?.colorized?.[0]?.colorName ?? ''
  const interiorFactoryColor = trimData.interior_factory_color ?? ''
  const fuelType = getTechSpecs(trimData, 'Engine', 'Engine Type', '')
  const transmission = getStdEquip(trimData, 'MECHANICAL', 'Transmission', '')

  return {
    '@context': 'https://schema.org',
    '@type': 'Vehicle',
    name: `${year} ${make} ${model}`,
    image: image,
    bodyType: vehicleType,
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      price: price,
      priceCurrency: 'USD',
    },
    itemCondition: `https://schema.org/${condition}Condition`,
    brand: {
      '@type': 'Brand',
      name: make,
    },
    model: model,
    vehicleConfiguration: trim ?? styleName,
    vehicleSeatingCapacity: passengerCapacity,
    driveWheelConfiguration: `https://schema.org/${drivetrain.replace(/\s/g, '')}Configuration`,
    vehicleModelDate: year,
    mileageFromOdometer: {
      '@type': 'QuantitativeValue',
      value: mileage,
      unitCode: 'SMI',
    },
    color: exteriorColor,
    vehicleInteriorColor: interiorFactoryColor,
    vehicleInteriorType: 'Standard',
    vehicleEngine: {
      '@type': 'EngineSpecification',
      fuelType: fuelType,
    },
    vehicleTransmission: transmission,
  }
}

const setSelectedTrim = (trimId: number) => {
  selectedTrimId.value = trimId

  if (trims.value == null) {
    selectedTrim.value = null
  }

  selectedTrim.value = trims.value[trimId]
}

const syncColorIndexWithNewColorSet = () => {
  if (currentColorHex.value !== '') {
    const colorIndex = colors.value.findIndex(
      (color: any) => color.colorValue === currentColorHex.value,
    )

    if (colorIndex !== -1) {
      selectedColorIndex.value = colorIndex
    } else {
      selectedColorIndex.value = 0
    }
  } else {
    selectedColorIndex.value = 0
  }

  updateCurrentColorInfo()
}

const updateCurrentColorInfo = () => {
  currentColorHex.value =
    colors.value[selectedColorIndex.value]?.colorValue ?? ''
  currentColorName.value =
    colors.value[selectedColorIndex.value]?.colorName ?? ''
}

const updateHeaderImages = async () => {
  headerCarImages.value = []

  for (const color of colors.value) {
    const colorValue = color.colorValue
    headerCarImages.value.push(
      getImageHeaderForTrim(selectedTrim.value, colorValue),
    )
  }
}

const updateHighlightedFeatures = () => {
  availableHighlightedFeatures.value = []

  if (selectedTrim.value) {
    currentVehicleName.value = `${selectedTrim.value.year} ${selectedTrim.value.make} ${selectedTrim.value.model}`

    const availableHighlightedFeaturesValues = extractHighlightedFeatures(
      selectedTrim.value,
    )

    Object.keys(availableHighlightedFeaturesMapping).forEach(key => {
      if (availableHighlightedFeaturesValues[key] !== 'Not Available') {
        availableHighlightedFeatures.value.push({
          key: availableHighlightedFeaturesMapping[key],
          value: availableHighlightedFeaturesValues[key],
        })
      }
    })
  }
}

const getImageHeaderForTrim = (trimData: any, colorHex: string): string => {
  const trimName = trimData.trim

  // Get colorized image from image store
  const headerImage = imageStore.getHeaderImage(trimName)
  const colorizedImage = imageStore.getColorizedHeaderImage(trimName, colorHex)
  const imageToUse = colorizedImage?.image ?? headerImage
  const imageIsFromCDN = imageToUse.indexOf(imageServiceUrl) >= 0

  if (!imageIsFromCDN) {
    window.setTimeout(async () => {
      const fetchedImage = await getImageAsync(
        trimData.styleId,
        trimData.make,
        trimData.model,
        trimName,
        colorHex,
      )

      if (fetchedImage == null) {
        return
      }

      if (colorizedImage != null) {
        imageStore.updateColorizedHeaderImage(trimName, colorHex, {
          ...colorizedImage,
          image: fetchedImage,
        })
      } else {
        imageStore.updateHeaderImage(trimName, fetchedImage)
      }
    }, 300)
  }

  return imageToUse
}

const selectTrim = async (trimId: number) => {
  // Update selected trim
  setSelectedTrim(trimId)

  if (selectedTrim.value == null) {
    return
  }

  // Update color listing
  colors.value = selectedTrim.value.images?.colorized ?? []

  // Check if current color is in new colors
  syncColorIndexWithNewColorSet()

  // Update header image
  const colorHex = colors.value[selectedColorIndex.value]?.colorValue ?? ''
  // const colorizedHeaderImage = imageStore.getColorizedHeaderImage(selectedTrim.value.trim, colorHex)
  //
  headerImage.value = getImageHeaderForTrim(selectedTrim.value, colorHex)

  // Get header images
  await updateHeaderImages()

  // Update highlighted features
  updateHighlightedFeatures()

  updateHeaderImage()
}

const selectColor = (colorId: string) => {
  selectedColorIndex.value = Number(colorId)
  updateCurrentColorInfo()
  updateHeaderImage()
}

const availableHighlightedFeatures = ref<
  Array<{ key: string; value: string | string[] }>
>([])

const extractHighlightedFeatures = (
  data: any,
): { [key: string]: string | string[] } => {
  return {
    horsepower:
      data?.technicalSpecification?.Engine?.items?.find((item: any) =>
        item.includes('SAE Net Horsepower'),
      ) || 'Not Available',
    torque:
      data?.technicalSpecification?.Engine?.items?.find((item: any) =>
        item.includes('SAE Net Torque'),
      ) || 'Not Available',
    transmission:
      data?.technicalSpecification?.Transmission?.items?.find((item: any) =>
        item.includes('Trans Description Cont.'),
      ) || 'Not Available',
    drivetrain:
      data?.technicalSpecification?.Transmission?.items?.find((item: any) =>
        item.includes('Drivetrain'),
      ) || 'Not Available',
    fuelEconomy:
      data?.technicalSpecification?.Mileage?.items?.find((item: any) =>
        item.includes('EPA MPG Equivalent - Combined'),
      ) || 'Not Available',
    chargingSpeed:
      data?.standardEquipment?.MECHANICAL?.items?.find((item: any) =>
        item.includes('Charge Time'),
      ) || 'Not Available',
    batteryWarranty:
      data?.consumerInformation?.Warranty?.items?.find((item: any) =>
        item.includes('Hybrid/Electric Components Years'),
      ) || 'Not Available',
    bodyStyle:
      data?.technicalSpecification?.Summary?.items?.find((item: any) =>
        item.includes('Body Style'),
      ) || 'Not Available',
    engineType:
      data?.technicalSpecification?.Engine?.items?.find((item: any) =>
        item.includes('Engine Type'),
      ) || 'Not Available',
    seatingCapacity:
      data?.technicalSpecification['Interior Dimensions']?.items?.find(
        (item: any) => item.includes('Passenger Capacity'),
      ) || 'Not Available',
    estimatedBatteryRange:
      data?.technicalSpecification.Mileage?.items?.find((item: any) =>
        item.includes('Estimated Battery Range'),
      ) || 'Not Available',
    legRoom:
      data?.technicalSpecification['Interior Dimensions']?.items?.filter(
        (item: any) => item.includes('Leg Room'),
      ) || 'Not Available',
    headRoom:
      data?.technicalSpecification['Interior Dimensions']?.items?.filter(
        (item: any) => item.includes('Head Room'),
      ) || 'Not Available',
    blindSpotMonitor:
      data?.standardEquipment?.SAFETY?.items?.find((item: any) =>
        item.includes('BLIS (Blind Spot Information System)'),
      ) || 'Not Available',
    laneKeepingAssist:
      data?.standardEquipment?.SAFETY?.items?.find((item: any) =>
        item.includes('Lane Keeping Alert Lane Keeping Assist'),
      ) || 'Not Available',
    heatedSeat:
      data?.availableOptions['ADDITIONAL EQUIPMENT']?.items?.find((item: any) =>
        item.includes('Heated Front Row Seats'),
      ) || 'Not Available',
  }
}

const updateHeaderImage = () => {
  const trimName = selectedTrim.value.trim
  const colorHex = currentColorHex.value
  const colorizedImage = imageStore.getColorizedHeaderImage(trimName, colorHex)
  const fallbackImage = imageStore.getHeaderImage(trimName) || noVehicleImage

  headerImage.value =
    colorizedImage?.image || colorizedImage?.fallbackImage || fallbackImage
}

const fetchHeaderImage = async (): Promise<string | null> => {
  return await getImageAsync(
    selectedTrim.value?.styleId,
    selectedTrim.value?.make,
    selectedTrim.value?.model,
    selectedTrim.value?.trim,
    currentColorHex.value,
  )
}

// Get header styles
const headerStyles: IHeaderSettings =
  globalSettings.value?.header ?? ({} as IHeaderSettings)

// Model font-size (mobile)
const modelNameFontSizeMobile = computed(() => {
  return getSizeAndUnitCSS(
    'fontSize',
    headerStyles?.modelName?.mobile ?? {
      fontSize: '14',
      fontSizeUnit: 'px',
    },
  )
})

// Model font-size (desktop)
const modelNameFontSizeDesktop = computed(() => {
  return getSizeAndUnitCSS(
    'fontSize',
    headerStyles?.modelName?.desktop ?? {
      fontSize: '16',
      fontSizeUnit: 'px',
    },
  )
})

// Trim font-size (mobile)
const trimStyleFontSizeMobile = computed(() => {
  return getSizeAndUnitCSS(
    'fontSize',
    headerStyles?.trimStyle?.mobile ?? {
      fontSize: '16',
      fontSizeUnit: 'px',
    },
  )
})

// Trim font-size (desktop)
const trimStyleFontSizeDesktop = computed(() => {
  return getSizeAndUnitCSS(
    'fontSize',
    headerStyles?.trimStyle?.desktop ?? {
      fontSize: '16',
      fontSizeUnit: 'px',
    },
  )
})

// Price font-size (mobile)
const priceFontSizeMobile = computed(() => {
  return getSizeAndUnitCSS(
    'fontSize',
    headerStyles?.price?.mobile ?? {
      fontSize: '16',
      fontSizeUnit: 'px',
    },
  )
})

// Price font-size (desktop)
const priceFontSizeDesktop = computed(() => {
  return getSizeAndUnitCSS(
    'fontSize',
    headerStyles?.price?.desktop ?? {
      fontSize: '16',
      fontSizeUnit: 'px',
    },
  )
})

// Get trim card styles
const trimCard: ComputedRef<any> = computed(
  () => globalSettings.value?.trimCard ?? {},
)
const trimCardWidth = computed(
  () => `${trimCard.value?.width}${trimCard.value?.widthUnit}`,
)
const trimCardHeight = computed(
  () => `${trimCard.value?.height}${trimCard.value?.heightUnit}`,
)
const trimCardImageMinHeight = computed(
  () => `${trimCard.value?.imageHeight}${trimCard.value?.imageHeightUnit}`,
)
const trimCardBorderRadius = computed(
  () => `${trimCard.value?.borderRadius}${trimCard.value?.borderRadiusUnit}`,
)

const initImageStore = () => {
  const trims = vehicleDetail.value?.trims ?? []

  for (const trimData of trims) {
    const trimName = trimData.trim || ''

    const trimToAdd: IHeaderImageStore = {
      name: trimName,
      colorizedImages: [],
      image: trimData.img || noVehicleImage,
    }

    // Add colorized images
    const colorizedImageData = trimData.images?.colorized ?? []

    if (colorizedImageData.length > 0) {
      const firstColorizedImage = getTrimColorizedImage(
        trimData,
        colorizedImageData[0].colorValue,
      )

      if (firstColorizedImage != null) {
        trimToAdd.image = firstColorizedImage
      }

      for (const colorizedImage of colorizedImageData) {
        const image =
          getTrimColorizedImage(trimData, colorizedImage.colorValue) ??
          noVehicleImage

        trimToAdd.colorizedImages.push({
          colorName: colorizedImage.colorName ?? '',
          colorValue: colorizedImage.colorValue ?? '',
          image: '',
          fallbackImage: image,
        })
      }
    } else {
      setTimeout(() => {
        const image = getImageHeaderForTrim(trimData, '')
        imageStore.updateHeaderImage(trimName, image)
      }, 300)
    }

    imageStore.addTrim(
      trimData.trim,
      trimToAdd.image,
      trimToAdd.colorizedImages,
    )
  }
}

const setVehicleDetail = async () => {
  const settings = pageSettings.value
  const year = Number(settings?.year ?? 0)
  const make = settings?.make
  const model = settings?.model
  const fallback = {
    year: '',
    make: '',
    model: '',
    trims: [],
  }

  if (year != null && make != null && model != null) {
    // Fetch model details
    const data = await fetchVehicleDetailByModel(year, make, model)

    // Set vehicle information to display
    vehicleDetail.value = data ?? fallback

    // Inject SEO Schema
    const trims = data?.trims ?? []

    for (const trim of trims) {
      injectSchema(getSchema(trim))
    }
  } else {
    vehicleDetail.value = fallback
  }
}

const preloadInitialTrimImages = async () => {
  const trims = vehicleDetail.value?.trims ?? []

  if (trims.length === 0) {
    return
  }

  const trimData = trims[0]
  const styleId = trimData.styleId ?? ''
  const make = trimData.make ?? ''
  const model = trimData.model ?? ''
  const trim = trimData.trim ?? ''
  const colorHex = colors.value[selectedColorIndex.value]?.colorValue ?? ''

  const image = await getImageAsync(styleId, make, model, trim, colorHex)

  if (image != null) {
    imageStore.addHeaderImage(trim, image)
  }
}

onMounted(async () => {
  console.log('>>>> PAGE', pageSettings.value)
  await setVehicleDetail()
  console.log('>>>> VEHICLE', vehicleDetail.value)
  initImageStore()
  await preloadInitialTrimImages()
  await selectTrim(0)

  console.log('Image Store', imageStore.getHeaderImages())
})
</script>

<style scoped>
.lightning-model-page__trims-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  width: 100%;
  padding: 0 16px;
}

.lightning-model-page__trim-wrapper {
  border-radius: v-bind('trimCardBorderRadius');
  display: flex;
  max-width: 100%;
  width: v-bind('trimCardWidth');
}

.lightning-model-page__trim-card {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.lightning-model-page__trim-card :deep(.v-card-text) {
  flex-grow: 1; /* Allow card content to grow and fill space */
  display: flex;
  flex-direction: column;
}

.lightning-model-page__trim-image-container {
  flex: 1 0 auto; /* Don't shrink the image container */
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  min-height: 200px;
}

.lightning-model-page__trim-info {
  margin-top: auto; /* Push info to bottom of card */
}

.lightning-model-page__banner {
  width: 100%;
  height: 300px;
  background-position: center;
  background-size: cover;
  position: relative;
}

.lightning-model-page__banner__car-image {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 30%);
  min-height: 400px;
}

.lightning-model-page__banner__car-image :deep(img) {
  height: 100% !important;
}

.lightning-model-page__header {
  margin-top: 50px;
}

.lightning-model-page__header .lightning-model-page__header__model-name {
  font-size: v-bind('modelNameFontSizeDesktop');
  line-height: 1em;
}

.lightning-model-page__header .lightning-model-page__header__trim-style {
  font-size: v-bind('trimStyleFontSizeDesktop');
  line-height: 1em;
}

.lightning-model-page__header .lightning-model-page__header__price {
  font-size: v-bind('priceFontSizeDesktop');
  line-height: 1em;
}

.lightning-model-page__CTAS-group {
  width: 100%;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

/* Trim Card */
.lightning-model-page__trim-card :deep(.v-img) {
  min-height: v-bind('trimCardImageMinHeight');
}

.lightning-model-page__trim-card.selected {
  border: 2px solid #bdbdbd;
}

.color--selected {
  outline: 2px solid #000;
  outline-offset: 1px;
}

.lightning-model-page__model-details
  :deep(.v-expansion-panels .v-expansion-panel button.v-expansion-panel-title) {
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
}

.lightning-model-page__model-details
  :deep(
    .v-expansion-panels .v-expansion-panel button.v-expansion-panel-title:hover
  ) {
  border: none;
  border-radius: 0 !important;
  background: transparent;
}

.lightning-model-page__model-details
  :deep(
    .v-expansion-panels .v-expansion-panel button.v-expansion-panel-title:focus
  ) {
  border: none;
  border-radius: 0 !important;
  outline: none;
}

@media all and (max-width: 767px) {
  .lightning-model-page__trim-wrapper {
    width: 100%;
    margin-bottom: 16px;
  }

  .lightning-model-page__banner {
    height: 39vw;
    min-height: 200px;
  }

  .lightning-model-page__header {
    font-size: 2.25vw;
    line-height: normal;
    margin-top: 5vw;
  }

  .lightning-model-page__header .lightning-model-page__header__model-name {
    font-size: v-bind('modelNameFontSizeMobile');
    text-align: center;
  }

  .lightning-model-page__header .lightning-model-page__header__trim-style {
    font-size: v-bind('trimStyleFontSizeMobile');
    line-height: 1em;
  }

  .lightning-model-page__header .lightning-model-page__header__price {
    font-size: v-bind('priceFontSizeMobile');
    text-align: center;
  }

  .lightning-model-page__banner__car-image {
    min-height: auto;
  }

  :deep(.lightning-model-page__highlighted-features__header) {
    font-size: 24px !important;
  }
}
</style>
